import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus, Edit, Trash2, Image } from 'lucide-react';
import { useMenuItems } from '../../../api/queries/menu.queries';
import { useCategories } from '../../../api/queries/menu.queries';
import { useDeleteMenuItem } from '../../../api/mutations/menu.mutations';
import ItemEditModal from './ItemEditModal';
import type { MenuItem } from '../../../api/queries/menu.queries';

function ItemManager() {
  const { t, i18n } = useTranslation();
  const { data: categories } = useCategories();
  const { data: menuItems, refetch } = useMenuItems();
  const [selectedCategory, setSelectedCategory] = useState<string>('');
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState<MenuItem | undefined>(undefined);

  const getLangValue = (multiLang: any) => {
    return multiLang?.[i18n.language as 'en' | 'th'] || multiLang?.en || '';
  };

  const filteredItems = selectedCategory
    ? menuItems?.filter((item) => item.categoryId === selectedCategory)
    : menuItems;

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('th-TH', {
      style: 'currency',
      currency: 'THB',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const handleCreate = () => {
    setEditingItem(undefined);
    setShowModal(true);
  };

  const handleEdit = (item: MenuItem) => {
    setEditingItem(item);
    setShowModal(true);
  };

  const handleSuccess = () => {
    refetch();
  };

  const handleDelete = async (itemId: string) => {
    if (confirm('Are you sure you want to delete this item?')) {
      const deleteMutation = useDeleteMenuItem(itemId);
      try {
        await deleteMutation.mutateAsync();
        refetch();
      } catch (error) {
        console.error('Failed to delete item:', error);
      }
    }
  };

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-bold">{t('menu.items')}</h2>
        <button
          onClick={handleCreate}
          className="rounded-lg bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 transition-colors flex items-center gap-2"
        >
          <Plus className="h-4 w-4" />
          {t('menu.createItem')}
        </button>
      </div>

      {/* Category filter */}
      <div className="flex items-center gap-2">
        <label className="text-sm font-medium">{t('menu.category')}:</label>
        <select
          value={selectedCategory}
          onChange={(e) => setSelectedCategory(e.target.value)}
          className="rounded-lg border bg-background px-3 py-1.5 text-sm"
        >
          <option value="">{t('common.all')}</option>
          {categories?.map((cat) => (
            <option key={cat._id} value={cat._id}>
              {getLangValue(cat.name)}
            </option>
          ))}
        </select>
      </div>

      {/* Items grid */}
      <div className="grid grid-cols-3 gap-4">
        {filteredItems?.map((item) => (
          <div key={item._id} className="rounded-lg border bg-card overflow-hidden">
            {/* Image */}
            <div className="relative aspect-square bg-muted flex items-center justify-center">
              {item.imageUrl ? (
                <img
                  src={item.imageUrl}
                  alt={getLangValue(item.name)}
                  className="w-full h-full object-cover"
                />
              ) : (
                <Image className="h-16 w-16 text-muted-foreground opacity-20" />
              )}
              {!item.isAvailable && (
                <div className="absolute inset-0 bg-black/50 flex items-center justify-center text-white font-medium text-sm">
                  {t('menu.unavailable') || 'Unavailable'}
                </div>
              )}
            </div>

            {/* Content */}
            <div className="p-3 space-y-2">
              <div>
                <div className="font-medium line-clamp-1">{getLangValue(item.name)}</div>
                {item.description && (
                  <div className="text-xs text-muted-foreground line-clamp-2">
                    {getLangValue(item.description)}
                  </div>
                )}
              </div>

              <div className="flex items-baseline gap-1">
                <span className="font-bold text-primary">{formatPrice(item.basePrice)}</span>
                {item.sizePricing && Object.keys(item.sizePricing).length > 0 && (
                  <span className="text-xs text-muted-foreground">+</span>
                )}
              </div>

              {/* Tags */}
              {item.tags && item.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {item.tags.slice(0, 2).map((tag) => (
                    <span
                      key={tag}
                      className="inline-flex rounded-full bg-secondary px-2 py-0.5 text-xs"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              )}

              {/* Actions */}
              <div className="flex gap-2 pt-2 border-t">
                <button
                  onClick={() => handleEdit(item)}
                  className="flex-1 rounded border bg-background px-2 py-1.5 text-xs hover:bg-secondary flex items-center justify-center gap-1"
                >
                  <Edit className="h-3 w-3" />
                  {t('common.edit')}
                </button>
                <button
                  onClick={() => handleDelete(item._id)}
                  className="rounded border border-destructive/20 bg-destructive/10 px-2 py-1.5 text-xs text-destructive hover:bg-destructive/20"
                >
                  <Trash2 className="h-3 w-3" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {filteredItems?.length === 0 && (
        <div className="text-center py-12 text-muted-foreground">
          <Image className="h-16 w-16 mx-auto mb-4 opacity-20" />
          <p>{t('menu.noItems') || 'No menu items found'}</p>
        </div>
      )}

      {/* Edit Modal */}
      {showModal && (
        <ItemEditModal
          item={editingItem}
          onClose={() => setShowModal(false)}
          onSuccess={handleSuccess}
        />
      )}
    </div>
  );
}

export default ItemManager;
